'use client'

import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'

import { useOverlayStack } from 'shared/contexts/OverlayStackContext'
import { useFocusTrap } from 'shared/ui/hooks/useFocusTrap'
import { KEYBOARD_KEYS, ZIndex } from 'shared/ui/types'
import { isClient } from 'shared/utils/common'

import { ClientOnlyPortal } from '../primitives/ClientOnlyPortal'

import ModalClose from './ModalClose'
import { ModalContext } from './ModalContext'

import styles from './Modal.module.scss'

interface ModalProps {
  id: string
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  description?: string
  closeOnBackdropClick?: boolean
  closeOnEscape?: boolean
  closeButton?: boolean
  closeButtonLabel?: string
  hideDefaultCloseButton?: boolean
  elementToReturnFocus?: React.RefObject<HTMLElement | null>
  size?: 'small' | 'medium' | 'large'
  initialFocusAnchor?: React.RefObject<HTMLElement | null>
}

export const Modal = ({
  id,
  open,
  onClose,
  children,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  hideDefaultCloseButton = false,
  size = 'medium',
  elementToReturnFocus,
  initialFocusAnchor,
  ...props
}: ModalProps) => {
  const [modalElement, setModalElement] = useState<HTMLDivElement | null>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const { register, unregister, isTop, getZIndex } = useOverlayStack()

  // Handle focus trap for accessibility and keyboard navigation
  useFocusTrap({
    container: modalElement,
    active: open,
    initialFocusAnchor: initialFocusAnchor?.current ?? null,
    previouslyFocusedElement: elementToReturnFocus
  })

  // Register/unregister modal in stack during lifecycle
  useEffect(() => {
    if (!open) return
    register({ id, type: 'modal' })

    return () => unregister(id)
  }, [open, id, register, unregister])

  // Handle closing on Escape key press
  useEffect(() => {
    if (!open || !closeOnEscape) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === KEYBOARD_KEYS.ESCAPE && isTop(id)) onClose()
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, closeOnEscape, onClose, isTop, id])

  const zIndex = useMemo(() => getZIndex(id) || ZIndex.MODAL, [getZIndex, id])

  const handleClickBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) onClose()
  }

  if (!open || !isClient()) return null

  return (
    <ClientOnlyPortal selector='#overlay-portal'>
      <ModalContext.Provider value={{ id, onClose }}>
        <div
          className={styles.backdrop}
          style={{ zIndex }}
          ref={backdropRef}
          onMouseDown={handleClickBackdrop}
          aria-hidden={false}
        >
          <div
            className={clsx(styles.container, styles[size])}
            ref={setModalElement}
            id={id}
            role='dialog'
            aria-modal='true'
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-description`}
            {...props}
          >
            <div className={styles.content}>
              {hideDefaultCloseButton ? null : <ModalClose />}
              {children}
            </div>
          </div>
        </div>
      </ModalContext.Provider>
    </ClientOnlyPortal>
  )
}

export default Modal
